/* mf-device.js — the remembered device, part 2 of 2.
 *
 * Part 1 is snippets/mf-device-head.liquid: the pre-paint hop on product pages and
 * its sixteen guards. This file is everything that happens AFTER paint:
 *
 *   · CAPTURE — the two places a visitor actually states a device: a row in the
 *     drawer's device takeover, and the device select on a product page. Both are
 *     delegated CAPTURE-phase listeners on documentElement, which is why neither
 *     snippets/glossy-drawer.liquid nor sections/product-rhode.liquid needs an edit
 *     (product-rhode is byte-identical to the live theme and stays that way), and
 *     why nothing downstream can stopPropagation the capture away. localStorage is
 *     synchronous, so the write always completes before the navigation.
 *
 *     Merely VIEWING a product page is deliberately NOT a capture point. An ad or a
 *     shared link must never silently overwrite a device the visitor chose — that is
 *     the whole difference between a memory and a tracker.
 *
 *   · THE DRAWER shows what is remembered, with one tap to clear it — the same
 *     affordance the gender tabs already have. A preference the visitor cannot see
 *     or undo is a bug, not a feature.
 *
 *   · THE OFFER — on the pages where a redirect would be wrong (ad landings, pasted
 *     links, back/forward) the memory still helps: a chip next to the device picker
 *     offers the switch instead of taking it.
 *
 *   · THE UNDO LINE on a page we did hop to, and dvm:undo, which is the single most
 *     important number here: the visitor telling us the hop was wrong.
 *
 * Storage: one localStorage key, mf_dev1, versioned, 180-day TTL enforced at read.
 * Not a cookie — Liquid cannot read cookies in this theme, so a cookie would buy no
 * server-side power and ride every request. Not mf_dev (taken by mf-feed.js) and not
 * mf:tinder:setup (taken by mf-tinder, and it holds a collection handle).
 * Measurement: dvm:* through mfTrackPush. NOT dev:*, which is an existing series in
 * mf-track.js for the device a PDP view was on — keeping them apart gives a free join.
 */
(function () {
  'use strict';

  var W = window, D = document;
  if (W.Shopify && W.Shopify.designMode) { return; }
  /* HU menu A/B (2026-09-12): capture, label and chip belong to the m1 drawer only */
  if ((W.__mfMN || '') !== 'm1') { return; }

  var KEY = 'mf_dev1';
  var TTL = 15552000000;                 /* 180 days */

  /* ── storage ─────────────────────────────────────────────────────────────── */
  function ls() { try { return W.localStorage; } catch (e) { return null; } }
  function ss() { try { return W.sessionStorage; } catch (e) { return null; } }

  function read() {
    var s = ls();
    if (!s) { return null; }
    try {
      var o = JSON.parse(s.getItem(KEY) || 'null');
      if (!o || o.v !== 1 || !o.k) { return null; }
      if (!o.t || (new Date().getTime() - o.t) > TTL) { s.removeItem(KEY); return null; }
      return o;
    } catch (e) { return null; }
  }

  function write(o) {
    var s = ls();
    if (!s) { return; }
    try { s.setItem(KEY, JSON.stringify(o)); } catch (e) {}
  }

  function clear() {
    var s = ls();
    if (!s) { return; }
    try { s.removeItem(KEY); } catch (e) {}
  }

  /* the head snippet publishes canon() on product pages; define the same one here
     so the drawer capture works on every other page type too */
  var BRANDS = { apple: 1, samsung: 1, galaxy: 1, google: 1, huawei: 1, xiaomi: 1,
                 redmi: 1, poco: 1, oneplus: 1, honor: 1, motorola: 1, oppo: 1,
                 realme: 1, vivo: 1, asus: 1, nothing: 1 };
  var canon = W.__mfDevCanon || function (t) {
    var x = String(t || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/^\s+|\s+$/g, '');
    if (!x) { return ''; }
    var parts = x.split(' '), i = 0;
    while (i < parts.length && BRANDS[parts[i]]) { i++; }
    var stripped = parts.slice(i).join('');
    return stripped.length >= 3 ? stripped : parts.join('');
  };

  function step(name, extra) {
    try {
      if (!W.mfTrackPush) { return; }
      var o = { mf_step: 'dvm:' + name };
      if (extra) { for (var k in extra) { if (Object.prototype.hasOwnProperty.call(extra, k)) { o[k] = extra[k]; } } }
      W.mfTrackPush('mf_nav_step', o);
    } catch (e) {}
  }

  function remember(label, url, via) {
    var k = canon(label);
    if (!k) { return; }
    var prev = read();
    write({
      v: 1,
      k: k,
      l: String(label).replace(/^\s+|\s+$/g, ''),
      c: (prev && prev.k === k && prev.c) ? prev.c : '',
      u: url || (prev && prev.k === k ? prev.u : '') || '',
      s: via,
      t: new Date().getTime()
    });
    step('set', { mf_device_label: String(label).slice(0, 40), mf_via: via });
    try { collectTargets(); paintMenuLinks(); } catch (e) {}
  }

  /* ── 1. capture: the drawer's device rows ────────────────────────────────── */
  D.documentElement.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a.mfd__devitem') : null;
    if (!a) { return; }
    remember(a.textContent || '', a.getAttribute('href') || '', 'mnu');
  }, true);

  /* ── 2. capture: every device select, on every product template ──────────── */
  var SELECTS = 'select[data-pr-device], #gpSel, #meta_dropdown, #meta_dropdown_sticky';
  D.documentElement.addEventListener('change', function (e) {
    var sel = e.target && e.target.closest ? e.target.closest(SELECTS) : null;
    if (!sel || !sel.options) { return; }
    var opt = sel.options[sel.selectedIndex];
    if (!opt) { return; }
    /* the LABEL, never the value — the value is a design-specific product handle
       and is useless as a stored device */
    remember(opt.text || '', '', 'pdp');
  }, true);

  /* ── 3. the drawer: show what is remembered, one tap to clear ────────────── */
  function paintDrawer() {
    var trigger = D.querySelector('.mfd__devtrigger');
    if (!trigger) { return; }
    var m = read();
    var val = trigger.querySelector('.mfd__devval');
    var x = trigger.querySelector('.mfd__devx');

    if (!m) {
      trigger.classList.remove('is-set');
      if (val) { val.parentNode.removeChild(val); }
      if (x) { x.parentNode.removeChild(x); }
      return;
    }

    trigger.classList.add('is-set');
    if (!val) {
      val = D.createElement('span');
      val.className = 'mfd__devval';
      trigger.appendChild(val);
    }
    val.textContent = m.l;
    var eyebrow = trigger.querySelector('span:not(.mfd__devval):not(.mfd__devx)');
    trigger.setAttribute('aria-label', ((eyebrow && eyebrow.textContent) || '') + ': ' + m.l);

    if (!x) {
      /* a SPAN, not a button: the trigger it sits inside is itself a <button>, and a
         button inside a button is invalid HTML that browsers resolve unpredictably */
      x = D.createElement('span');
      x.className = 'mfd__devx';
      x.setAttribute('role', 'button');
      x.setAttribute('tabindex', '0');
      x.setAttribute('aria-label', 'Mentett telefon törlése');
      x.innerHTML = '&#10005;';
      var wipe = function (ev) {
        ev.preventDefault();
        ev.stopPropagation();               /* must not open the takeover */
        clear();
        step('clear');
        paintDrawer();
        markCurrent();
        paintMenuLinks();
      };
      x.addEventListener('click', wipe);
      x.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter' || ev.key === ' ' || ev.key === 'Spacebar') { wipe(ev); }
      });
      trigger.appendChild(x);
    }
  }

  /* mark the remembered row inside the takeover; never reorder the DOM, because row
     order feeds the menu's data-mx-pos measurement */
  function markCurrent() {
    var m = read();
    var rows = D.querySelectorAll('a.mfd__devitem');
    var found = null;
    [].forEach.call(rows, function (a) {
      var on = !!m && (canon(a.textContent || '') === m.k ||
                       (!!m.u && a.getAttribute('href') === m.u));
      a.classList.toggle('is-current', on);
      if (on) { a.setAttribute('aria-current', 'page'); } else { a.removeAttribute('aria-current'); }
      if (on && !found) { found = a; }
    });
    return found;
  }

  D.documentElement.addEventListener('click', function (e) {
    var o = e.target && e.target.closest ? e.target.closest('[data-mfd-open="device"]') : null;
    if (!o) { return; }
    setTimeout(function () {
      var row = markCurrent();
      if (row && row.scrollIntoView) {
        try { row.scrollIntoView({ block: 'center' }); } catch (e2) { row.scrollIntoView(); }
      }
    }, 60);
  }, false);

  /* ── 3b. the menu follows the remembered phone ───────────────────────────── */
  /* Reported twice by the merchant: with an iPhone 16 remembered, the menu still landed
     on iPhone 17 Pro Max pages. Every mood, drop and collab collection on this store is
     single-device, so a menu link is a device link in disguise.

     There is exactly one URL shape that combines the two, and it is device-first:
     /collections/<device>/<tag>. The reverse fails OPEN - /collections/edgy/iphone-16
     301s back to the mood collection and serves 200 with the wrong phones - so it must
     never be built. An unknown tag also 301s, to the bare device collection.

     WHAT IS REWRITTEN, and why not everything. Measured on iPhone 16:
       moods        /collections/iphone-16/edgy         25 pages   - the mood name IS the tag
       TOP 100      /collections/iphone-16/best-sellers  5 pages   - via the map below
       FALL DROPS   /collections/iphone-16/all-drops    12 pages   - via the map below
       (naive)      /collections/iphone-16/top100        301 - no such tag
       (naive)      /collections/iphone-16/fall-drop     301 - no such tag
     The campaign collections have NO tag of their own: the fall drop's 21 products split
     across 'Fuzzy Season' (11) and 'Amber Autumn' (10) with nothing covering all of them,
     and a tag path cannot express OR. So a link is rewritten only where a tag is KNOWN to
     reproduce it. Everywhere else - the collabs above all - the link is left alone and the
     landing page says the truth instead, because silently swapping a collab for "all
     designs on your phone" throws away the whole point of the collab.

     THE DURABLE FIX IS DATA, NOT CODE: give every campaign collection one tag named after
     its handle, applied across devices, and this map can be deleted - /collections/<device>/<handle>
     would then work for all of them. */
  var TAGMAP = W.__mfDevTagMap || {
    'top100': 'best-sellers',
    'fall-drop': 'all-drops'
  };

  function pathOf(href) {
    return String(href || '').split('?')[0].split('#')[0].replace(/\/+$/, '');
  }

  function collHandle(path) {
    var i = path.indexOf('/collections/');
    if (i !== 0) { return ''; }
    var rest = path.slice(13);
    if (!rest || rest.indexOf('/') > -1) { return ''; }   /* already tag-scoped */
    return rest;
  }

  function deviceCollection() {
    var m = read();
    if (!m) { return ''; }
    var u = m.u || '';
    if (u.indexOf('/collections/') !== 0) {
      /* chosen on a product page, so no collection URL came with it. The drawer renders
         every device link on every page - resolve one now and heal the stored value. */
      var rows = D.querySelectorAll('a.mfd__devitem');
      for (var i = 0; i < rows.length; i++) {
        if (canon(rows[i].textContent || '') !== m.k) { continue; }
        var h = rows[i].getAttribute('href') || '';
        if (h.indexOf('/collections/') !== 0) { continue; }
        u = h; m.u = h; write(m);
        break;
      }
    }
    if (u.indexOf('/collections/') !== 0) { return ''; }
    return pathOf(u);
  }

  /* every menu link we know how to point at another phone: path -> tag */
  var rewritable = {};

  function collectTargets() {
    [].forEach.call(D.querySelectorAll('a[data-mfd-mood]'), function (a) {
      var orig = a.getAttribute('data-mfd-href0');
      if (!orig) { orig = pathOf(a.getAttribute('href')); a.setAttribute('data-mfd-href0', orig); }
      var tag = a.getAttribute('data-mfd-mood') || '';
      if (orig && tag) { rewritable[orig] = tag; a.setAttribute('data-mfd-tag', tag); }
    });
    [].forEach.call(D.querySelectorAll('a.glossy-drawer-menu-link[href]'), function (a) {
      if (a.classList.contains('mfd__devitem')) { return; }      /* a device row itself */
      if (a.hasAttribute('data-mfd-tag')) { return; }
      var orig = a.getAttribute('data-mfd-href0') || pathOf(a.getAttribute('href'));
      var h = collHandle(orig);
      if (!h || !TAGMAP[h]) { return; }
      a.setAttribute('data-mfd-href0', orig);
      a.setAttribute('data-mfd-tag', TAGMAP[h]);
      rewritable[orig] = TAGMAP[h];
    });
  }

  function paintMenuLinks() {
    var dev = deviceCollection();
    [].forEach.call(D.querySelectorAll('a[data-mfd-tag]'), function (a) {
      var orig = a.getAttribute('data-mfd-href0') || a.getAttribute('href') || '';
      var tag = a.getAttribute('data-mfd-tag') || '';
      a.setAttribute('href', (dev && tag && pathOf(orig) !== dev) ? (dev + '/' + tag) : orig);
    });
  }

  /* Arriving on someone else's phone any other way - a shared link, a search result, an
     ad - does NOT redirect, for the same reason the product page does not. It says so. */
  function collectionRescue() {
    var dev = deviceCollection();
    if (!dev) { return; }
    var here = pathOf(W.location.pathname);
    if (!collHandle(here)) { return; }        /* already tag-scoped, or not a collection */
    if (here === dev) { return; }
    var m = read();
    if (!m) { return; }

    var grid = D.querySelector('[id^="collection-grid-"] .product-list__inner');
    if (!grid || !grid.parentNode) { return; }

    var tag = rewritable[here] || '';
    var el = D.createElement('div');
    el.className = 'mf-devchip';
    var txt = D.createElement('span');
    var a = D.createElement('a');

    if (tag) {
      txt.textContent = 'Ezek a minták nem ' + m.l + ' készülékhez valók.';
      a.href = dev + '/' + tag + W.location.search;
      a.textContent = 'Mutasd ' + m.l + ' készülékhez →';
      a.addEventListener('click', function () { step('coll_go'); });
    } else {
      /* no tag reproduces this collection on another phone - say what IS true: the
         designs are shown on a different phone, and the product page hands over yours */
      txt.textContent = 'A minták más telefonon látszanak, de ' + m.l + ' készülékre kapod.';
      a.href = dev;
      a.textContent = 'Összes ' + m.l + ' készülékhez →';
      a.addEventListener('click', function () { step('coll_all'); });
    }

    el.appendChild(txt);
    el.appendChild(a);
    grid.parentNode.insertBefore(el, grid);
    step(tag ? 'coll_pill' : 'coll_note');
  }

  /* ── 4. product page: the undo line, the offer chip, the denominators ────── */
  function pdpAnchor() {
    var root = D.querySelector('[data-pr-device-root]');
    if (!root) { return null; }
    return root.closest('.pr-row') || root;
  }

  function note(cls) {
    var host = pdpAnchor();
    if (!host || !host.parentNode) { return null; }
    var el = D.createElement('div');
    el.className = cls;
    host.parentNode.insertBefore(el, host.nextSibling);
    return el;
  }

  function landedNotice() {
    var s = ss();
    if (!s) { return false; }
    var hop = null;
    try { hop = JSON.parse(s.getItem('mf_devhop') || 'null'); } catch (e) { return false; }
    if (!hop || hop.to !== (W.__mfDevH || '')) { return false; }
    try { s.removeItem('mf_devhop'); } catch (e2) {}

    step('hop', { mf_device_from: String(hop.from || '').slice(0, 40),
                  mf_device_to: String(hop.to || '').slice(0, 40),
                  mf_device_label: String(hop.l || '').slice(0, 40) });

    var el = note('mf-devnote');
    if (!el) { return true; }
    var txt = D.createElement('span');
    txt.textContent = 'Átváltottunk erre: ' + (hop.l || 'a telefonod') + '.';
    var a = D.createElement('a');
    a.href = '/products/' + hop.from + '?mf_nohop=1';
    a.textContent = 'Mutasd inkább: ' + (hop.fl || 'az előző modell');
    a.addEventListener('click', function () { step('undo'); });
    var x = D.createElement('button');
    x.type = 'button';
    x.className = 'mf-devnote__x';
    x.setAttribute('aria-label', 'Bezárás');
    x.innerHTML = '&#10005;';
    x.addEventListener('click', function () { el.parentNode && el.parentNode.removeChild(el); });
    el.appendChild(txt);
    el.appendChild(a);
    el.appendChild(x);
    return true;
  }

  function offerChip() {
    var hit = W.__mfDevHit, m = read();
    if (!hit || !m) { return; }
    var el = note('mf-devchip');
    if (!el) { return; }
    var txt = D.createElement('span');
    txt.textContent = 'A telefonod: ' + m.l;
    var a = D.createElement('a');
    a.href = '/products/' + hit.h;
    a.textContent = 'váltás →';
    a.addEventListener('click', function () { step('pill'); });
    el.appendChild(txt);
    el.appendChild(a);
  }

  function boot() {
    collectTargets();
    paintDrawer();
    markCurrent();
    paintMenuLinks();
    collectionRescue();

    var state = W.__mfDevState || '';
    if (!landedNotice()) {
      if (state === 'offer') { offerChip(); }
      else if (state === 'miss') { step('miss'); }
      else if (state === 'on') { step('on'); }
    }
  }

  if (D.readyState === 'loading') { D.addEventListener('DOMContentLoaded', boot); } else { boot(); }
})();
